import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  BookOpenIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { getStudentDashboard, StudentDashboardData } from '@/api/dashboardApi'
import toast from 'react-hot-toast'

const StudentDashboard: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<StudentDashboardData | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const response = await getStudentDashboard()
      if (response.success && response.data) {
        setDashboardData(response.data)
      }
    } catch (error) {
      console.error('Dashboard error:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const activeBorrowings = dashboardData?.library?.activeBorrowings || 0
  const overdueBorrowings = dashboardData?.library?.overdueBorrowings || 0
  const totalBorrowed = dashboardData?.library?.totalBorrowed || 0
  const pendingFinesAmount = dashboardData?.library?.pendingFines?.amount || 0
  const pendingFinesCount = dashboardData?.library?.pendingFines?.count || 0
  const walletBalance = dashboardData?.wallet?.balance || 0
  const recentBorrowings = dashboardData?.recentBorrowings || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <AcademicCapIcon className="h-8 w-8 text-blue-600 mr-3" />
            Welcome, {user?.firstName}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here's your student dashboard overview
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Today's Date</p>
          <p className="text-lg font-semibold text-gray-900">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {overdueBorrowings > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-xl p-4"
        >
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Overdue Books Alert</h3>
              <p className="text-sm text-red-700">
                You have {overdueBorrowings} overdue book{overdueBorrowings > 1 ? 's' : ''}. Please return them to avoid additional fines.
              </p>
            </div>
            <Link
              to="/student/library"
              className="ml-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              View Details
            </Link>
          </div>
        </motion.div>
      )}

      {pendingFinesCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4"
        >
          <div className="flex items-center space-x-3">
            <CurrencyDollarIcon className="h-6 w-6 text-amber-600" />
            <div>
              <h3 className="font-semibold text-amber-900">Pending Fines</h3>
              <p className="text-sm text-amber-700">
                You have {pendingFinesCount} unpaid fine{pendingFinesCount > 1 ? 's' : ''} totaling {formatCurrency(pendingFinesAmount)}.
              </p>
            </div>
            <Link
              to="/student/wallet"
              className="ml-auto px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
            >
              Pay Now
            </Link>
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Borrowings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Borrowings</p>
              <p className="text-2xl font-bold text-gray-900">{activeBorrowings}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpenIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3">
            <Link to="/student/library" className="text-sm text-blue-600 hover:text-blue-800 flex items-center">
              View books <ArrowRightIcon className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </motion.div>

        {/* Overdue Books */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue Books</p>
              <p className={`text-2xl font-bold ${overdueBorrowings > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {overdueBorrowings}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${overdueBorrowings > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              {overdueBorrowings > 0 ? (
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              ) : (
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              )}
            </div>
          </div>
          <div className="mt-3">
            <span className={`text-sm ${overdueBorrowings > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {overdueBorrowings > 0 ? 'Please return soon' : 'All books on time'}
            </span>
          </div>
        </motion.div>

        {/* Pending Fines */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Fines</p>
              <p className={`text-2xl font-bold ${pendingFinesAmount > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                {formatCurrency(pendingFinesAmount)}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${pendingFinesAmount > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
              <CurrencyDollarIcon className={`h-6 w-6 ${pendingFinesAmount > 0 ? 'text-amber-600' : 'text-green-600'}`} />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-sm text-gray-500">{pendingFinesCount} fine{pendingFinesCount !== 1 ? 's' : ''}</span>
          </div>
        </motion.div>

        {/* Wallet Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Wallet Balance</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(walletBalance)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3">
            <Link to="/student/wallet" className="text-sm text-green-600 hover:text-green-800 flex items-center">
              View wallet <ArrowRightIcon className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Borrowings */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BookOpenIcon className="h-5 w-5 text-blue-600 mr-2" />
              Recent Borrowings
            </h3>
            <Link to="/student/library" className="text-sm text-blue-600 hover:text-blue-800">
              View All
            </Link>
          </div>

          {recentBorrowings.length === 0 ? (
            <div className="text-center py-8">
              <BookOpenIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No borrowing history</p>
              <Link to="/student/library/browse" className="mt-2 inline-block text-blue-600 hover:text-blue-800">
                Browse Library
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentBorrowings.map((borrowing, index) => (
                <div
                  key={borrowing._id || index}
                  className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-12 h-16 bg-gray-200 rounded flex-shrink-0 overflow-hidden">
                    {borrowing.book?.coverImage ? (
                      <img
                        src={borrowing.book.coverImage}
                        alt={borrowing.book.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpenIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{borrowing.book?.title || 'Unknown Title'}</p>
                    <p className="text-sm text-gray-500">{borrowing.book?.authors?.join(', ') || 'Unknown Author'}</p>
                    <div className="flex items-center mt-1 text-xs">
                      <CalendarDaysIcon className="h-3 w-3 mr-1 text-gray-400" />
                      <span className="text-gray-500">Due: {formatDate(borrowing.dueDate)}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    borrowing.status === 'active' || borrowing.status === 'renewed'
                      ? 'bg-blue-100 text-blue-800'
                      : borrowing.status === 'overdue'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {borrowing.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Library Summary</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <BookOpenIcon className="h-8 w-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Total Books Borrowed</p>
                  <p className="text-xl font-bold text-gray-900">{totalBorrowed}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Currently Borrowed</p>
                  <p className="text-xl font-bold text-gray-900">{activeBorrowings}</p>
                </div>
              </div>
            </div>

            <div className={`flex items-center justify-between p-4 rounded-lg ${
              overdueBorrowings > 0 ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              <div className="flex items-center">
                <ClockIcon className={`h-8 w-8 mr-3 ${overdueBorrowings > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm text-gray-600">Overdue Books</p>
                  <p className={`text-xl font-bold ${overdueBorrowings > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {overdueBorrowings}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h4>
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/student/library/browse"
                className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <BookOpenIcon className="h-4 w-4 mr-2" />
                Browse Books
              </Link>
              <Link
                to="/student/wallet"
                className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                My Wallet
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default StudentDashboard
